import type { ReactNode } from "react";

/**
 * Isolates a run of user data from the sentence around it.
 *
 * This is the fix for a bug that only exists in mixed-direction text, and it is
 * invisible to anybody testing in English.
 *
 * The Unicode bidirectional algorithm decides the display order of a line from
 * the characters in it, and a run of Latin characters inside an Arabic sentence
 * is laid out left to right — correctly — but **the punctuation around it has no
 * direction of its own** and joins whichever neighbour wins. So an Arabic
 * sentence containing `SKU-1042` can render the identifier as `1042-SKU`, or
 * drag a trailing colon to the wrong end, or reorder two SKUs in a list. The
 * data is intact; the pixels are wrong; and nobody can copy the value off the
 * screen and have it match the label on the carton.
 *
 * `<bdi>` tells the browser to work out that run's direction on its own and
 * keep it from leaking into the sentence. It is one element, it needs no CSS,
 * and it is the whole fix.
 *
 * **Use it for anything a person or another system typed**: SKUs, order codes,
 * names, addresses, phone numbers, file names, email addresses. Not for our own
 * translated strings — those are already in the locale's direction, and wrapping
 * them is noise. `DataTable`'s monospace cells use this by default (EP00-S09),
 * because an identifier column is the case this exists for.
 */

export interface BdiProps {
	children: ReactNode;
	className?: string;
	/**
	 * Force a direction instead of letting the browser infer one.
	 *
	 * Almost never needed. `auto` — the default, and what `<bdi>` does on its
	 * own — reads the first strong character, which is right for real data. Set
	 * it only when a value's direction is known and its first character is not
	 * strong: a phone number that begins with `+`, say.
	 */
	dir?: "ltr" | "rtl" | "auto";
}

export function Bdi({ children, className, dir = "auto" }: BdiProps) {
	return (
		<bdi dir={dir} className={className}>
			{children}
		</bdi>
	);
}
